/**
 * Line Detail Panel — Slide-out Drawer.
 *
 * A polished right-side drawer that displays all detail fields for a
 * purchase order line item: asset identification (year, make, model,
 * variant, serial number, VIN, lot number), delivery & tracking,
 * status & receiving, fees, and notes.
 *
 * All fields are editable in Draft/Submitted mode and read-only once
 * received. Changes auto-save on blur.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Package,
  Truck,
  FileText,
  Tag,
  Hash,
  ClipboardCheck,
  CreditCard,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import type { PurchaseOrderLine } from "@/types/purchase-order";
import type { OrderTaxLine } from "@/types/sales-order";
import { useOrgConfig } from "@/features/settings/api";
import type { NamingConvention } from "@/types/settings";
import Badge from "@/components/ui/Badge";
import LineChargesSection from "@/components/ui/LineChargesSection";
import OrderLevelChargesList from "@/components/ui/OrderLevelChargesList";
import Button from "@/components/ui/Button";
import CategorySelect from "@/components/ui/CategorySelect";

const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    v,
  );

const statusVariant: Record<
  string,
  "default" | "success" | "warning" | "danger" | "info"
> = {
  pending: "default",
  on_order: "info",
  shipped: "warning",
  received: "success",
  cancelled: "danger",
};

interface PaymentAllocationForLine {
  payment_id: string;
  payment_method: string;
  payment_date: string;
  amount: number;
}

interface LineDetailPanelProps {
  line: PurchaseOrderLine;
  poExpectedDate: string | null;
  poId?: string;
  isOpen: boolean;
  onClose: () => void;
  canEdit: boolean;
  onUpdateLine: (data: Record<string, unknown>) => void;
  onAddCharge?: (data: {
    charge_type: string;
    description: string;
    amount: number;
  }) => void;
  onDeleteCharge?: (chargeId: string) => void;
  isAddingCharge?: boolean;
  onReceiveLine?: () => void;
  isReceiving?: boolean;
  /** Order-level tax lines for read-only display. */
  orderTaxLines?: OrderTaxLine[];
}

/**
 * Auto-saving input field that commits on blur.
 *
 * Tracks its own local value so edits don't flicker, and only fires
 * onSave when the value actually changed.
 */
function AutoSaveInput({
  value,
  onSave,
  placeholder,
  type = "text",
  className,
  maxLength,
  disabled,
}: {
  value: string;
  onSave: (v: string) => void;
  placeholder?: string;
  type?: string;
  className?: string;
  maxLength?: number;
  disabled?: boolean;
}) {
  const [local, setLocal] = useState(value);
  const pristine = useRef(true);

  useEffect(() => {
    setLocal(value);
    pristine.current = true;
  }, [value]);

  return (
    <input
      type={type}
      value={local}
      onChange={(e) => {
        setLocal(e.target.value);
        pristine.current = false;
      }}
      onBlur={() => {
        if (!pristine.current && local !== value) onSave(local);
      }}
      placeholder={placeholder}
      maxLength={maxLength}
      disabled={disabled}
      className={className}
    />
  );
}

function LineDetailPanel({
  line,
  poExpectedDate,
  poId,
  isOpen,
  onClose,
  canEdit,
  onUpdateLine,
  onAddCharge,
  onDeleteCharge,
  isAddingCharge,
  onReceiveLine,
  isReceiving,
  orderTaxLines,
}: LineDetailPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const { data: config } = useOrgConfig();

  // Fetch payments with allocations for this line.
  const { data: linePayments } = useQuery<PaymentAllocationForLine[]>({
    queryKey: ["line-payments", poId, line.id],
    queryFn: async () => {
      if (!poId) return [];
      const res = await apiClient.get(
        `/api/v1/payments?entity_type=purchase_order&entity_id=${poId}`,
      );
      const payments = res.data as Array<{
        id: string;
        payment_method: string;
        payment_date: string;
        allocations: Array<{ line_id: string; amount: number }>;
      }>;
      const result: PaymentAllocationForLine[] = [];
      for (const p of payments) {
        for (const a of p.allocations || []) {
          if (a.line_id === line.id) {
            result.push({
              payment_id: p.id,
              payment_method: p.payment_method,
              payment_date: p.payment_date,
              amount: Number(a.amount),
            });
          }
        }
      }
      return result;
    },
    enabled: isOpen && !!poId,
    staleTime: 30_000,
  });

  const totalAllocated = (linePayments || []).reduce((s, p) => s + p.amount, 0);

  const convention: NamingConvention = useMemo(
    () =>
      config?.products?.naming_convention || {
        components: [
          { key: "year", builtin: true },
          { key: "make", builtin: true },
          { key: "model", builtin: true },
          { key: "category", builtin: true },
        ],
        separator: " ",
      },
    [config],
  );

  const getCategoryLabel = useCallback(
    (value: string): string => {
      const cats =
        config?.products?.asset_categories ||
        config?.inventory?.asset_categories ||
        [];
      const search = (
        list: Array<{ value: string; label: string; children?: unknown[] }>,
      ): string | null => {
        for (const c of list) {
          if (c.value === value) return c.label;
          if (c.children) {
            const found = search(c.children as typeof list);
            if (found) return found;
          }
        }
        return null;
      };
      return search(cats) || value;
    },
    [config],
  );

  const buildAssetName = useCallback(
    (fields: {
      year?: number | null;
      make?: string | null;
      model?: string | null;
      variant?: string | null;
      category?: string;
    }) => {
      const fieldMap: Record<string, string | undefined> = {
        year: fields.year ? String(fields.year) : undefined,
        make: fields.make || undefined,
        model: fields.model || undefined,
        variant: fields.variant || undefined,
        category: fields.category
          ? getCategoryLabel(fields.category)
          : undefined,
      };
      return convention.components
        .map((c) => fieldMap[c.key])
        .filter(Boolean)
        .join(convention.separator || " ");
    },
    [convention, getCategoryLabel],
  );

  // Lot number preview for auto-generation.
  const { data: lotPreview } = useQuery<{ lot_number: string }>({
    queryKey: ["next-lot-number"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/assets/next-lot-number")).data,
    enabled: isOpen && !line.lot_number,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const isEditable = canEdit && line.line_status !== "received";

  const inputClass =
    "h-[34px] w-full rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-2.5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)] focus-visible:border-[var(--border-focus)] transition-shadow";
  const readonlyClass = "text-sm text-[var(--text-primary)]";
  const labelClass =
    "block text-[11px] font-medium text-[var(--text-secondary)] mb-1";
  const sectionClass = "px-5 py-4";
  const sectionDivider = "border-b border-[var(--border-subtle)]";

  const canReceive =
    onReceiveLine &&
    (line.line_status === "on_order" || line.line_status === "shipped");

  // Determine the line total including charges.
  const chargesTotal = (line.charges || []).reduce(
    (s, c) => s + Number(c.amount),
    0,
  );
  const lineAllIn =
    Number(line.line_total) ||
    Number(line.total_price) + Number(line.line_fees) + chargesTotal;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/25 backdrop-blur-[2px] animate-fade-in"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className="fixed inset-y-0 right-0 z-50 w-full max-w-[420px] bg-[var(--surface-base)] shadow-2xl border-l border-[var(--border-default)] flex flex-col animate-slide-in-right"
      >
        {/* ── Header ── */}
        <div className="px-5 pt-4 pb-3 border-b border-[var(--border-default)]">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h3 className="text-[15px] font-semibold text-[var(--text-primary)] leading-snug truncate">
                {line.asset_name || line.description}
              </h3>
              <div className="flex items-center gap-2 mt-1.5">
                <Badge variant={statusVariant[line.line_status] || "default"}>
                  {line.line_status.replace("_", " ")}
                </Badge>
                <span className="text-xs text-[var(--text-tertiary)] capitalize">
                  {line.category}
                </span>
                {line.quantity > 1 && (
                  <span className="text-xs text-[var(--text-tertiary)]">
                    &times; {line.quantity}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-sunken)] transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Quick summary bar */}
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[var(--border-subtle)]">
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                Unit price
              </p>
              <p className="text-sm font-semibold tabular-nums text-[var(--text-primary)]">
                {fmt(Number(line.unit_price))}
              </p>
            </div>
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                Line total
              </p>
              <p className="text-sm font-semibold tabular-nums text-[var(--text-primary)]">
                {fmt(Number(line.total_price))}
              </p>
            </div>
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                All-in
              </p>
              <p className="text-sm font-bold tabular-nums text-[var(--color-interactive-primary)]">
                {fmt(lineAllIn)}
              </p>
            </div>
          </div>
        </div>

        {/* ── Scrollable content ── */}
        <div className="flex-1 overflow-y-auto">
          {/* Section: Asset Identification */}
          <div className={`${sectionClass} ${sectionDivider}`}>
            <div className="flex items-center gap-1.5 mb-3">
              <Tag className="h-3.5 w-3.5 text-[var(--text-tertiary)]" />
              <h4 className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                Asset Identification
              </h4>
            </div>
            <div className="grid grid-cols-3 gap-x-3 gap-y-3">
              <div>
                <label className={labelClass}>Year</label>
                {isEditable ? (
                  <AutoSaveInput
                    value={line.year ? String(line.year) : ""}
                    onSave={(v) => {
                      const yr = v ? parseInt(v) : null;
                      const name = buildAssetName({
                        year: yr,
                        make: line.make,
                        model: line.model,
                        variant: line.variant,
                        category: line.category,
                      });
                      onUpdateLine({ year: yr, asset_name: name || null });
                    }}
                    type="number"
                    placeholder="e.g., 2024"
                    className={`${inputClass} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                  />
                ) : (
                  <p className={readonlyClass}>{line.year || "—"}</p>
                )}
              </div>
              <div>
                <label className={labelClass}>Make</label>
                {isEditable ? (
                  <AutoSaveInput
                    value={line.make || ""}
                    onSave={(v) => {
                      const name = buildAssetName({
                        year: line.year,
                        make: v || null,
                        model: line.model,
                        variant: line.variant,
                        category: line.category,
                      });
                      onUpdateLine({
                        make: v || null,
                        asset_name: name || null,
                      });
                    }}
                    placeholder="e.g., Ford"
                    className={inputClass}
                  />
                ) : (
                  <p className={readonlyClass}>{line.make || "—"}</p>
                )}
              </div>
              <div>
                <label className={labelClass}>Model</label>
                {isEditable ? (
                  <AutoSaveInput
                    value={line.model || ""}
                    onSave={(v) => {
                      const name = buildAssetName({
                        year: line.year,
                        make: line.make,
                        model: v || null,
                        variant: line.variant,
                        category: line.category,
                      });
                      onUpdateLine({
                        model: v || null,
                        asset_name: name || null,
                      });
                    }}
                    placeholder="e.g., F150"
                    className={inputClass}
                  />
                ) : (
                  <p className={readonlyClass}>{line.model || "—"}</p>
                )}
              </div>
              <div className="col-span-3">
                <label className={labelClass}>Variant</label>
                {isEditable ? (
                  <AutoSaveInput
                    value={line.variant || ""}
                    onSave={(v) => {
                      const name = buildAssetName({
                        year: line.year,
                        make: line.make,
                        model: line.model,
                        variant: v || null,
                        category: line.category,
                      });
                      onUpdateLine({
                        variant: v || null,
                        asset_name: name || null,
                      });
                    }}
                    placeholder="e.g., XLT, Premium, MFWD"
                    className={inputClass}
                  />
                ) : (
                  <p className={readonlyClass}>{line.variant || "—"}</p>
                )}
              </div>
              <div className="col-span-3">
                <label className={labelClass}>Category</label>
                {isEditable ? (
                  <CategorySelect
                    value={line.category}
                    onChange={(v) => {
                      if (v !== line.category) {
                        const name = buildAssetName({
                          year: line.year,
                          make: line.make,
                          model: line.model,
                          variant: line.variant,
                          category: v,
                        });
                        onUpdateLine({ category: v, asset_name: name || null });
                      }
                    }}
                  />
                ) : (
                  <p className={`${readonlyClass} capitalize`}>
                    {getCategoryLabel(line.category)}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Section: Serial / VIN / Lot */}
          <div className={`${sectionClass} ${sectionDivider}`}>
            <div className="flex items-center gap-1.5 mb-3">
              <Hash className="h-3.5 w-3.5 text-[var(--text-tertiary)]" />
              <h4 className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                Identification Numbers
              </h4>
            </div>
            <div className="space-y-3">
              <div>
                <label className={labelClass}>Serial Number</label>
                {isEditable ? (
                  <AutoSaveInput
                    value={line.serial_number || ""}
                    onSave={(v) => onUpdateLine({ serial_number: v || null })}
                    placeholder="Manufacturer serial number"
                    className={inputClass}
                  />
                ) : (
                  <p className={`${readonlyClass} font-mono`}>
                    {line.serial_number || "—"}
                  </p>
                )}
              </div>
              <div>
                <label className={labelClass}>VIN</label>
                {isEditable ? (
                  <AutoSaveInput
                    value={line.vin || ""}
                    onSave={(v) => onUpdateLine({ vin: v || null })}
                    placeholder="17-character VIN"
                    maxLength={17}
                    className={`${inputClass} font-mono uppercase tracking-wider`}
                  />
                ) : (
                  <p
                    className={`${readonlyClass} font-mono uppercase tracking-wider`}
                  >
                    {line.vin || "—"}
                  </p>
                )}
              </div>
              <div>
                <label className={labelClass}>Lot Number</label>
                {isEditable ? (
                  <div className="flex items-center gap-2">
                    <AutoSaveInput
                      value={line.lot_number || ""}
                      onSave={(v) => onUpdateLine({ lot_number: v || null })}
                      placeholder={
                        lotPreview?.lot_number
                          ? `Auto: ${lotPreview.lot_number}`
                          : "Lot number"
                      }
                      className={`${inputClass} flex-1 font-mono`}
                    />
                    {!line.lot_number && lotPreview?.lot_number && (
                      <button
                        type="button"
                        onClick={() =>
                          onUpdateLine({ lot_number: lotPreview.lot_number })
                        }
                        className="shrink-0 h-[34px] px-2.5 rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-sunken)] text-[10px] font-semibold text-[var(--color-interactive-primary)] hover:bg-[var(--fill-info)] transition-colors"
                        title="Assign the next lot number"
                      >
                        Auto
                      </button>
                    )}
                  </div>
                ) : (
                  <p className={`${readonlyClass} font-mono`}>
                    {line.lot_number || "—"}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Section: Delivery & Tracking */}
          <div className={`${sectionClass} ${sectionDivider}`}>
            <div className="flex items-center gap-1.5 mb-3">
              <Truck className="h-3.5 w-3.5 text-[var(--text-tertiary)]" />
              <h4 className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                Delivery & Tracking
              </h4>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Expected Delivery</label>
                {isEditable ? (
                  <div>
                    <input
                      type="date"
                      value={line.expected_delivery_date || ""}
                      onChange={(e) =>
                        onUpdateLine({
                          expected_delivery_date: e.target.value || null,
                        })
                      }
                      className={inputClass}
                    />
                    {poExpectedDate &&
                      line.expected_delivery_date &&
                      line.expected_delivery_date.slice(0, 10) !==
                        poExpectedDate.slice(0, 10) && (
                        <button
                          type="button"
                          onClick={() =>
                            onUpdateLine({
                              expected_delivery_date: poExpectedDate,
                            })
                          }
                          className="text-[10px] text-[var(--color-interactive-primary)] hover:underline mt-1"
                        >
                          Reset to PO date
                        </button>
                      )}
                  </div>
                ) : (
                  <p className={readonlyClass}>
                    {line.expected_delivery_date
                      ? new Date(
                          line.expected_delivery_date,
                        ).toLocaleDateString()
                      : "—"}
                  </p>
                )}
              </div>
              <div>
                <label className={labelClass}>Tracking Number</label>
                {isEditable ? (
                  <AutoSaveInput
                    value={line.tracking_number || ""}
                    onSave={(v) => onUpdateLine({ tracking_number: v || null })}
                    placeholder="Enter tracking #"
                    className={inputClass}
                  />
                ) : (
                  <p className={readonlyClass}>{line.tracking_number || "—"}</p>
                )}
              </div>
            </div>
          </div>

          {/* Section: Status & Receiving */}
          <div className={`${sectionClass} ${sectionDivider}`}>
            <div className="flex items-center gap-1.5 mb-3">
              <ClipboardCheck className="h-3.5 w-3.5 text-[var(--text-tertiary)]" />
              <h4 className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                Status & Receiving
              </h4>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Line Status</label>
                <Badge variant={statusVariant[line.line_status] || "default"}>
                  {line.line_status.replace("_", " ")}
                </Badge>
              </div>
              <div>
                <label className={labelClass}>Received</label>
                <p className={readonlyClass}>
                  {line.received_at
                    ? new Date(line.received_at).toLocaleDateString()
                    : "—"}
                </p>
              </div>
            </div>
            {canReceive && (
              <div className="mt-3">
                <Button
                  size="sm"
                  onClick={onReceiveLine}
                  isLoading={isReceiving}
                >
                  <Package className="h-3.5 w-3.5" /> Receive This Item
                </Button>
              </div>
            )}
          </div>

          {/* Section: Fees & Charges */}
          <div className={`${sectionClass} ${sectionDivider}`}>
            <LineChargesSection
              line={line}
              isEditable={isEditable}
              onAddCharge={onAddCharge}
              onDeleteCharge={onDeleteCharge}
              isAddingCharge={isAddingCharge}
              legacyFee={{
                amount: Number(line.line_fees) || 0,
                description: line.line_fee_description,
                onClear: () =>
                  onUpdateLine({ line_fees: 0, line_fee_description: null }),
              }}
            />
            {orderTaxLines && orderTaxLines.length > 0 && (
              <OrderLevelChargesList lines={orderTaxLines} className="mt-5" />
            )}
          </div>

          {/* Section: Payment Status */}
          {poId && (linePayments || []).length > 0 && (
            <div className={`${sectionClass} ${sectionDivider}`}>
              <div className="flex items-center gap-1.5 mb-3">
                <CreditCard className="h-3.5 w-3.5 text-[var(--text-tertiary)]" />
                <h4 className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                  Payments Allocated
                </h4>
              </div>
              <div className="space-y-0">
                {(linePayments || []).map((p, i) => (
                  <div
                    key={`${p.payment_id}-${i}`}
                    className="flex items-center justify-between py-1.5 border-b border-[var(--border-subtle)]/50 last:border-0"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Badge variant="info">
                        {p.payment_method.replace("_", " ")}
                      </Badge>
                      <span className="text-[11px] text-[var(--text-tertiary)]">
                        {new Date(p.payment_date).toLocaleDateString()}
                      </span>
                    </div>
                    <span className="text-xs tabular-nums font-medium text-[var(--text-success)] shrink-0">
                      {fmt(p.amount)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-[var(--border-default)]">
                <span className="text-[11px] font-medium text-[var(--text-secondary)]">
                  Total Allocated
                </span>
                <span className="text-sm tabular-nums font-semibold text-[var(--text-primary)]">
                  {fmt(totalAllocated)}
                </span>
              </div>
              {lineAllIn > 0 && (
                <div className="mt-1.5 h-1.5 rounded-full bg-[var(--surface-sunken)] overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      totalAllocated >= lineAllIn
                        ? "bg-[var(--color-success)]"
                        : "bg-[var(--color-interactive-primary)]"
                    }`}
                    style={{
                      width: `${Math.min((totalAllocated / lineAllIn) * 100, 100)}%`,
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Section: Notes */}
          <div className={sectionClass}>
            <div className="flex items-center gap-1.5 mb-3">
              <FileText className="h-3.5 w-3.5 text-[var(--text-tertiary)]" />
              <h4 className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                Notes
              </h4>
            </div>
            {isEditable ? (
              <textarea
                rows={3}
                defaultValue={line.notes || ""}
                placeholder="Additional details about this item..."
                onBlur={(e) => {
                  if (e.target.value !== (line.notes || ""))
                    onUpdateLine({ notes: e.target.value || null });
                }}
                className="w-full rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-2.5 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)] focus-visible:border-[var(--border-focus)] resize-y transition-shadow"
              />
            ) : (
              <p className="text-sm text-[var(--text-secondary)]">
                {line.notes || "No notes."}
              </p>
            )}
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="border-t border-[var(--border-default)] px-5 py-3 bg-[var(--surface-sunken)]/30">
          <div className="flex items-center justify-between">
            <span className="text-xs text-[var(--text-secondary)]">
              Total (incl. fees & charges)
            </span>
            <span className="text-base font-bold tabular-nums text-[var(--text-primary)]">
              {fmt(lineAllIn)}
            </span>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

export default LineDetailPanel;
